import { z } from 'zod';
/** Egyptian National ID: 14 digits. Digit 1 is the century marker (2 = 1900s, 3 = 2000s). */
export const EGYPTIAN_NATIONAL_ID_REGEX = /^[23]\d{13}$/;
/** Accepts 01XXXXXXXXX and the +20 international form. */
export const EGYPTIAN_PHONE_REGEX = /^(?:\+?20|0)1[0125]\d{8}$/;
/** Field names shown in API and admin errors instead of raw schema paths. */
const FIELD_LABELS = {
    name: 'Name',
    fullName: 'Name',
    staffFullName: 'Staff name',
    city: 'City',
    addressLine: 'Address',
    phone: 'Phone',
    staffPhone: 'Staff phone',
    contactPhone: 'Contact phone',
    email: 'Email',
    staffEmail: 'Login email',
    contactEmail: 'Contact email',
    password: 'Password',
    staffPassword: 'Password',
    currentPassword: 'Current password',
    newPassword: 'New password',
    title: 'Title',
    body: 'Text',
    bio: 'Bio',
    specialty: 'Specialty',
    description: 'Description',
    university: 'University',
    nationalId: 'National ID',
    mapUrl: 'Map link',
    question: 'Question',
    answer: 'Answer',
    startDate: 'Start date',
    endDate: 'End date',
    daysOfWeek: 'Days of the week',
    dailyStartTime: 'Daily start time',
    dailyEndTime: 'Daily end time',
    capacityPerSession: 'Capacity',
    sessionLengthHours: 'Session length',
    clinicLocationId: 'Branch',
    clinicLocationIds: 'Branches',
    compareAtPriceEgp: 'Compare-at price',
    priceEgp: 'Price',
    comment: 'Comment',
    universityIdFile: 'University ID photo',
    categoryId: 'Category',
    partnerTermsText: 'Partner terms',
    studentPolicyText: 'Clinical rules',
};
export function fieldLabel(path) {
    const last = path.split('.').filter(Boolean).pop() ?? path;
    if (FIELD_LABELS[last])
        return FIELD_LABELS[last];
    if (!last)
        return '';
    return last
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .replace(/^\w/, c => c.toUpperCase());
}
export function withFieldLabel(path, message) {
    const label = fieldLabel(path);
    if (!label)
        return message;
    if (message.toLowerCase().includes(label.toLowerCase()))
        return message;
    return `${label}: ${message}`;
}
export function formatZodIssue(issue) {
    const path = issue.path.map(String).join('.');
    return { path, message: withFieldLabel(path, issue.message) };
}
export function friendlyZodIssues(error) {
    return error.issues.map(formatZodIssue);
}
export function firstFriendlyZodMessage(error) {
    return friendlyZodIssues(error)[0]?.message ?? 'Check the form and try again.';
}
export const friendlyZodErrorMap = (issue, ctx) => {
    switch (issue.code) {
        case 'too_small':
            if (issue.type === 'string') {
                if (issue.minimum === 1)
                    return { message: 'This can’t be blank.' };
                return { message: `Use at least ${issue.minimum} characters.` };
            }
            if (issue.type === 'array') {
                if (issue.minimum === 1)
                    return { message: 'Add at least one item.' };
                return { message: `Add at least ${issue.minimum} items.` };
            }
            if (issue.type === 'number' || issue.type === 'bigint') {
                return { message: `Use a number of at least ${issue.minimum}.` };
            }
            break;
        case 'too_big':
            if (issue.type === 'string')
                return { message: `Keep this under ${issue.maximum} characters.` };
            if (issue.type === 'number' || issue.type === 'bigint') {
                return { message: `Use a number no more than ${issue.maximum}.` };
            }
            break;
        case 'invalid_string':
            if (issue.validation === 'email')
                return { message: 'Enter a valid email address.' };
            if (issue.validation === 'url')
                return { message: 'Enter a valid web address, starting with https://.' };
            if (issue.validation === 'uuid')
                return { message: 'That ID is not valid.' };
            if (issue.validation === 'datetime')
                return { message: 'Enter a valid date and time.' };
            if (issue.validation === 'regex')
                return { message: 'That value is not in the right format.' };
            break;
        case 'invalid_type':
            if (issue.received === 'undefined' || issue.received === 'null') {
                return { message: 'This is required.' };
            }
            return { message: 'That value is not in the right format.' };
        case 'invalid_enum_value':
            return { message: 'Pick one of the allowed options.' };
        case 'unrecognized_keys':
            return { message: 'Remove extra fields that are not allowed.' };
        default:
            break;
    }
    return { message: ctx.defaultError };
};
z.setErrorMap(friendlyZodErrorMap);
export const nationalIdSchema = z
    .string()
    .trim()
    .regex(EGYPTIAN_NATIONAL_ID_REGEX, 'Enter a valid 14-digit Egyptian National ID');
export const phoneSchema = z
    .string()
    .trim()
    .regex(EGYPTIAN_PHONE_REGEX, 'Enter a valid Egyptian mobile number, like 01XXXXXXXXX');
/** Treat blank form fields as omitted so optional emails/phones do not fail validation. */
export function emptyToUndefined(value) {
    if (typeof value === 'string' && value.trim() === '')
        return undefined;
    return value;
}
export const emailSchema = z
    .string()
    .trim()
    .min(1, 'Email is required')
    .toLowerCase()
    .email('Enter a valid email address');
export const optionalEmailSchema = z.preprocess(emptyToUndefined, emailSchema.optional());
export const optionalPhoneSchema = z.preprocess(emptyToUndefined, phoneSchema.optional());
/** Visual + server rules for registration / password change. */
export const PASSWORD_RULES = [
    { id: 'length', label: 'At least 8 characters', test: (p) => p.length >= 8 },
    { id: 'lower', label: 'One lowercase letter', test: (p) => /[a-z]/.test(p) },
    { id: 'upper', label: 'One uppercase letter', test: (p) => /[A-Z]/.test(p) },
    { id: 'number', label: 'One number', test: (p) => /[0-9]/.test(p) },
    { id: 'symbol', label: 'One symbol', test: (p) => /[^A-Za-z0-9]/.test(p) },
];
export function passwordMeetsAllRules(password) {
    return PASSWORD_RULES.every((rule) => rule.test(password));
}
/** Password that satisfies PASSWORD_RULES. Safe in the browser and Node. */
export function generateCompliantPassword(length = 12) {
    const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
    const bytes = new Uint8Array(length);
    if (typeof globalThis.crypto?.getRandomValues === 'function') {
        globalThis.crypto.getRandomValues(bytes);
    }
    else {
        for (let i = 0; i < length; i += 1)
            bytes[i] = Math.floor(Math.random() * 256);
    }
    let out = '';
    for (let i = 0; i < length; i += 1)
        out += alphabet[bytes[i] % alphabet.length];
    return `${out}#1a`;
}
export const passwordSchema = z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .max(200, 'Password is too long')
    .refine((p) => /[a-z]/.test(p), 'Password must include a lowercase letter')
    .refine((p) => /[A-Z]/.test(p), 'Password must include an uppercase letter')
    .refine((p) => /[0-9]/.test(p), 'Password must include a number')
    .refine((p) => /[^A-Za-z0-9]/.test(p), 'Password must include a symbol');
export const optionalPasswordSchema = z.preprocess(emptyToUndefined, passwordSchema.optional());
export const nameSchema = z.string().trim().min(2, 'Enter at least 2 characters').max(120, 'Keep this under 120 characters');
export const optionalNameSchema = z.preprocess(emptyToUndefined, nameSchema.optional());
/** Auto-generated codes use a prefix plus a name slug; admins may set any unique slug. */
export const referralCodeSchema = z
    .string()
    .trim()
    .toLowerCase()
    .min(2, 'Enter at least 2 characters')
    .max(40, 'Keep the referral code under 40 characters')
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Use letters, numbers, and hyphens only');
/** Empty optional referral field on signup forms. */
export const optionalReferralCodeSchema = z
    .union([z.literal(''), referralCodeSchema])
    .optional()
    .transform((value) => (value === '' || value === undefined ? undefined : value));
/** Random 8-digit numeric certificate id, displayed as #certXXXXXXXX. */
export const certificateIdSchema = z.string().trim().regex(/^\d{8}$/, 'Certificate ID must be 8 digits');
export const ratingScoreSchema = z.number().int().min(0).max(5);
/** YYYY-MM-DD, no time component. Used for internship dates. */
export const dateOnlySchema = z
    .string()
    .trim()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Enter a date as YYYY-MM-DD');
/** HH:MM 24-hour. Used for session time windows. */
export const timeOfDaySchema = z
    .string()
    .trim()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Enter a time as HH:MM');
/** 0 = Sunday .. 6 = Saturday, matching JS getDay(). */
export const dayOfWeekSchema = z.number().int().min(0).max(6);
export const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
/** Saturday-first labels, matching Egyptian internship weeks. */
export function formatWeekdayNames(daysOfWeek) {
    const set = new Set(daysOfWeek);
    return [6, 0, 1, 2, 3, 4, 5]
        .filter(d => set.has(d))
        .map(d => WEEKDAY_SHORT[d])
        .join(' · ');
}
export function normalizePhone(phone) {
    const trimmed = phone.trim();
    if (trimmed.startsWith('+20'))
        return `0${trimmed.slice(3).replace(/\D/g, '')}`;
    if (trimmed.startsWith('20') && trimmed.replace(/\D/g, '').length === 12) {
        return `0${trimmed.replace(/\D/g, '').slice(2)}`;
    }
    return trimmed;
}
/** WhatsApp chat URL. Egyptian 01… numbers become country-code 20… */
export function toWhatsAppUrl(phone) {
    let digits = phone.replace(/\D/g, '');
    if (digits.startsWith('00'))
        digits = digits.slice(2);
    if (digits.startsWith('0'))
        digits = `20${digits.slice(1)}`;
    if (digits.length < 10)
        return null;
    return `https://wa.me/${digits}`;
}
//# sourceMappingURL=validation.js.map